import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { BookOpen, X } from 'lucide-react';

export const WelcomeModal = ({ organization, isOpen, onClose }) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (isOpen && organization?.welcome_message) {
            // Check if user has seen this message before
            const seenKey = `welcome_seen_${organization.id}`;
            const hasSeenMessage = localStorage.getItem(seenKey);
            
            if (!hasSeenMessage) {
                setShow(true);
            }
        }
    }, [isOpen, organization]);

    const handleClose = () => {
        if (organization?.id) {
            localStorage.setItem(`welcome_seen_${organization.id}`, 'true');
        }
        setShow(false);
        onClose?.();
    };

    const handleShowAgain = () => {
        // Allow user to see message again by not saving to localStorage
        setShow(false);
        onClose?.();
    };

    if (!organization?.welcome_message) return null;

    return (
        <Dialog open={show} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: organization.primary_color || '#3b82f6' }}
                        >
                            <BookOpen className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl">
                                Willkommen bei {organization.name}!
                            </DialogTitle>
                            <DialogDescription>
                                Anleitung zur Nutzung des Empfehlungsportals
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                
                <div className="py-4">
                    <div 
                        className="prose prose-sm max-w-none text-slate-600"
                        dangerouslySetInnerHTML={{ __html: formatMessage(organization.welcome_message) }}
                    />
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button
                        variant="outline"
                        onClick={handleShowAgain}
                        className="w-full sm:w-auto"
                    >
                        Später erneut anzeigen
                    </Button>
                    <Button
                        onClick={handleClose}
                        className="w-full sm:w-auto"
                        style={{ backgroundColor: organization.primary_color || '#3b82f6' }}
                    >
                        Verstanden, los geht's!
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Helper to format plain text with line breaks
const formatMessage = (text) => {
    if (!text) return '';
    
    // Convert line breaks to <br> and paragraphs
    return text
        .split('\n\n')
        .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
        .join('');
};

// Button to manually show the welcome message
export const ShowWelcomeButton = ({ organization, onClick }) => {
    if (!organization?.welcome_message) return null;

    return (
        <Button
            variant="ghost"
            size="sm"
            onClick={onClick}
            className="text-slate-500 hover:text-slate-700"
        >
            <BookOpen className="w-4 h-4 mr-2" />
            Anleitung anzeigen
        </Button>
    );
};
